/* eslint-disable jsx-a11y/anchor-is-valid */
import {useState} from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import {useFormik} from 'formik'
import {login as login_api, LoginParams} from '../core/_requests'
import {Link} from 'react-router-dom'
import {SchoolModel} from '../../apps/school/schools-list/core/_models'
import {useAuth} from '../core/AuthContext'
import {Select} from '../../../../_component/Select'

const loginSchema = Yup.object().shape({
  email: Yup.string()
    .min(3, 'Minimum 3 symbols')
    .max(255, 'Maximum 255 symbols')
    .required('Username is required'),
  password: Yup.string()
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Password is required'),
})

const initialValues: LoginParams = {
  email: process.env.NODE_ENV === 'development' ? 'tobilasinde2012@yahoo.com' : '',
  password: process.env.NODE_ENV === 'development' ? process.env.REACT_APP_PASSWORD || '' : '',
}

/*
  Formik+YUP+Typescript:
  https://jaredpalmer.com/formik/docs/tutorial#getfieldprops
  https://medium.com/@maurice.de.beijer/yup-validation-and-typescript-and-formik-6c342578a20e
*/

export function Login() {
  const {login, logout, school} = useAuth()
  const [loading, setLoading] = useState(false)
  const [schools, setSchools] = useState<SchoolModel[]>()
  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      setLoading(true)
      try {
        if (!values.school_id) values.school_id = school?.id
        const data = await login_api({...values})
        if (data?.token_type === 'Bearer') login(data?.auth_token, data?.refresh_token)
        else if (data?.token_type === 'Basic') {
          setSchools(data?.schools)
          values.token = data?.token
        }
      } catch (error: any) {
        console.error(error)
        logout()
        setStatus(error.response.data.message || 'The login detail is incorrect')
        setSubmitting(false)
      } finally {
        setLoading(false)
      }
    },
  })

  return (
    <form
      className='form w-100'
      onSubmit={formik.handleSubmit}
      noValidate
      id='kt_login_signin_form'
    >
      {schools ? (
        <>
          {/* begin::Heading */}
          <div className='text-center mb-10'>
            <h1 className='text-dark mb-3'>Select a school to login into</h1>
          </div>
          {/* begin::Heading */}

          {formik.status && (
            <div className='mb-lg-15 alert alert-danger'>
              <div className='alert-text font-weight-bold'>{formik.status}</div>
            </div>
          )}
          {/* begin::Action */}
          <Select
            name='school_id'
            placeholder='Select School...'
            onSelect={(val: any) => formik.setFieldValue('school_id', val.value)}
            error={!!formik.getFieldMeta('school_id').error}
            formik={formik}
            value={formik.values.school_id}
            options={schools.map((item) => {
              return {label: item.name!, value: item.id}
            })}
            type='select'
          />
          <button
            type='submit'
            id='kt_sign_in_submit'
            className='btn btn-lg btn-primary w-100 my-5'
            disabled={formik.isSubmitting || !formik.isValid}
          >
            <span className='indicator-progress' style={{display: 'block'}}>
              <span className='indicator-label'>Submit</span>
              {loading && formik.values.school_id && (
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              )}
            </span>
          </button>
          {/* end::Action */}
        </>
      ) : (
        <>
          {/* begin::Heading */}
          <div className='text-center mb-10'>
            <h1 className='text-dark mb-3'>Sign In {school ? 'to ' + school.name : ''}</h1>
          </div>
          {/* begin::Heading */}

          {formik.status && (
            <div className='mb-lg-15 alert alert-danger'>
              <div className='alert-text font-weight-bold'>{formik.status}</div>
            </div>
          )}

          {/* begin::Form group */}
          <div className='fv-row mb-10'>
            <label className='form-label fs-6 fw-bolder text-dark'>
              Username / Phone Number / Admission Number
            </label>
            <input
              {...formik.getFieldProps('email')}
              className={clsx(
                'form-control form-control-lg form-control-solid',
                {'is-invalid': formik.touched.email && formik.errors.email},
                {
                  'is-valid': formik.touched.email && !formik.errors.email,
                }
              )}
              name='email'
              autoComplete='off'
            />
            {formik.touched.email && formik.errors.email && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert'>{formik.errors.email}</span>
                </div>
              </div>
            )}
          </div>
          {/* end::Form group */}

          {/* begin::Form group */}
          <div className='fv-row mb-10'>
            <div className='d-flex justify-content-between mt-n5'>
              <div className='d-flex flex-stack mb-2'>
                {/* begin::Label */}
                <label className='form-label fw-bolder text-dark fs-6 mb-0'>Password</label>
                {/* end::Label */}
                {/* begin::Link */}
                <Link
                  to='/auth/forgot-password'
                  className='link-primary fs-6 fw-bolder'
                  style={{marginLeft: '5px'}}
                >
                  Forgot Password ?
                </Link>
                {/* end::Link */}
              </div>
            </div>
            <input
              type='password'
              autoComplete='off'
              {...formik.getFieldProps('password')}
              className={clsx(
                'form-control form-control-lg form-control-solid',
                {
                  'is-invalid': formik.touched.password && formik.errors.password,
                },
                {
                  'is-valid': formik.touched.password && !formik.errors.password,
                }
              )}
            />
            {formik.touched.password && formik.errors.password && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert'>{formik.errors.password}</span>
                </div>
              </div>
            )}
          </div>
          {/* end::Form group */}

          {/* begin::Action */}
          <div className='text-center'>
            <button
              type='submit'
              id='kt_sign_in_submit'
              className='btn btn-lg btn-primary w-100 mb-5'
              disabled={formik.isSubmitting || !formik.isValid}
            >
              {!loading && <span className='indicator-label'>Continue</span>}
              {loading && (
                <span className='indicator-progress' style={{display: 'block'}}>
                  Please wait...
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              )}
            </button>
          </div>
          {/* end::Action */}
        </>
      )}
    </form>
  )
}
